import { useState, useMemo } from 'react';
import { Registro } from '../types';
import dayjs from 'dayjs';

// Permite valores iniciales para periodo, dateRange y filtroActivo
export const useFilteredRegistros = (
  registros: Registro[],
  initialPeriodo?: string,
  initialDateRange?: { startDate: string; endDate: string },
  initialFiltroActivo?: 'periodo' | 'rango'
) => {
  const [filtroActivo, setFiltroActivo] = useState<'periodo' | 'rango'>(
    initialFiltroActivo || 'periodo'
  );
  const [periodo, setPeriodo] = useState(initialPeriodo || '24');
  const [dateRange, setDateRange] = useState<{
    startDate: string;
    endDate: string;
  }>(
    initialDateRange || {
      startDate: new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString().split('T')[0],
      endDate: new Date().toISOString().split('T')[0],
    }
  );
  // Nueva opción para control de orden, true = ascendente (viejo a nuevo), false = descendente (nuevo a viejo)
  const [ordenAscendente, setOrdenAscendente] = useState(false);

  // Cambia el filtro activo automáticamente al seleccionar periodo o rango personalizado
  const setPeriodoConFiltro = (value: string) => {
    setPeriodo(value);
    if (value === 'Personalizado') {
      setFiltroActivo('rango');
    } else {
      setFiltroActivo('periodo');
    }
  };

  // Permite actualizar el rango y filtrar automáticamente
  const setDateRangeConFiltro = (range: { startDate: string; endDate: string }) => {
    setDateRange(range);
    setFiltroActivo('rango');
  };

  // Calcula el rango de fechas según el periodo seleccionado
  const getPeriodRange = (periodo: string) => {
    const today = dayjs();
    switch (periodo) {
      case '24':
        return {
          startDate: today.subtract(24, 'hour').format('YYYY-MM-DD'),
          endDate: today.format('YYYY-MM-DD'),
        };
      case '7d':
        return {
          startDate: today.subtract(7, 'day').format('YYYY-MM-DD'),
          endDate: today.format('YYYY-MM-DD'),
        };
      case '15d':
        return {
          startDate: today.subtract(15, 'day').format('YYYY-MM-DD'),
          endDate: today.format('YYYY-MM-DD'),
        };
      case '30d':
        return {
          startDate: today.subtract(30, 'day').format('YYYY-MM-DD'),
          endDate: today.format('YYYY-MM-DD'),
        };
      case 'hoy':
        return {
          startDate: today.startOf('day').format('YYYY-MM-DD'),
          endDate: today.endOf('day').format('YYYY-MM-DD'),
        };
      case 'ayer':
        return {
          startDate: today.subtract(1, 'day').startOf('day').format('YYYY-MM-DD'),
          endDate: today.subtract(1, 'day').endOf('day').format('YYYY-MM-DD'),
        };
      case 'semana':
        return {
          startDate: today.startOf('week').format('YYYY-MM-DD'),
          endDate: today.endOf('week').format('YYYY-MM-DD'),
        };
      case 'mes':
        return {
          startDate: today.startOf('month').format('YYYY-MM-DD'),
          endDate: today.endOf('month').format('YYYY-MM-DD'),
        };
      default:
        const hours = parseInt(periodo);
        if (!isNaN(hours)) {
          return {
            startDate: dayjs().subtract(hours, 'hour').format('YYYY-MM-DD'),
            endDate: today.format('YYYY-MM-DD'),
          };
        }
        return { startDate: '', endDate: '' };
    }
  };

  // Filtrado por periodo (hoy, ayer, semana, mes, últimas N horas)
  const filterByPeriod = (records: Registro[]) => {
    const { startDate, endDate } = getPeriodRange(periodo);
    if (!startDate || !endDate) return records;
    return records.filter(record => {
      const recordDate = dayjs(record.fecha).format('YYYY-MM-DD');
      return recordDate >= startDate && recordDate <= endDate;
    });
  };

  // Filtrado por rango personalizado
  const filterByDateRange = (records: Registro[]) => {
    if (!dateRange.startDate || !dateRange.endDate) return records;
    return records.filter(record => {
      const recordDate = dayjs(record.fecha).format('YYYY-MM-DD');
      return recordDate >= dateRange.startDate && recordDate <= dateRange.endDate;
    });
  };

  const registrosFiltrados = useMemo(() => {
    let filtered =
      filtroActivo === 'periodo' ? filterByPeriod(registros) : filterByDateRange(registros);
    // Solo en la página de registros, SOLO si filtroActivo es 'rango' y periodo es 'Personalizado'
    if (
      typeof window !== 'undefined' &&
      (window.location.pathname === '/' || window.location.pathname.includes('/registros')) &&
      filtroActivo === 'rango' &&
      periodo === 'Personalizado'
    ) {
      // Orden ascendente (del más viejo al más nuevo) para la página de registros con rango personalizado
      return filtered.sort((a, b) => new Date(a.fecha).getTime() - new Date(b.fecha).getTime());
    }
    // Para todo lo demás, descendente (más nuevo a más viejo)
    return filtered.sort((a, b) =>
      ordenAscendente
        ? new Date(a.fecha).getTime() - new Date(b.fecha).getTime()
        : new Date(b.fecha).getTime() - new Date(a.fecha).getTime()
    );
  }, [registros, filtroActivo, periodo, dateRange, ordenAscendente]);

  return {
    filtroActivo,
    setFiltroActivo,
    periodo,
    setPeriodo: setPeriodoConFiltro,
    dateRange,
    setDateRange: setDateRangeConFiltro,
    ordenAscendente,
    setOrdenAscendente,
    registrosFiltrados,
  };
};
